import { FAVICON_URL, SITE_NAME, getSiteUrl } from '@/lib/seo';
import type { ArticleContent } from '@/mocks/articles';

function inject(id: string, data: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function injectSiteJsonLd(): void {
  const site = getSiteUrl();

  inject('site-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${site}/`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${site}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  });

  inject('org-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${site}/`,
    logo: FAVICON_URL,
  });
}

export function injectArticleJsonLd(article: ArticleContent, url: string, lang: string): void {
  inject('route-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.metaDescription,
    image: [article.heroImg],
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    datePublished: '2026-08-01',
    dateModified: '2026-08-26',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: lang,
  });
}

const FAQ_ITEMS = [
  {
    q: 'What is the AllChinaBuy Spreadsheet?',
    a: 'The AllChinaBuy Spreadsheet is an independent informational resource that organizes product research, seller information, shipping guides, and buying tutorials for users researching AllChinaBuy products.',
  },
  {
    q: 'Is AllChinaBuy legit?',
    a: 'AllChinaBuy is a known shopping agent platform. We provide independent guides and resources to help users make informed decisions when using such platforms.',
  },
  {
    q: 'Is AllChinaBuy safe?',
    a: 'Safety depends on following best practices: verifying links, using secure payment methods, checking seller reviews, and understanding the platform\'s policies. Our Safety Center has detailed guidance.',
  },
  {
    q: 'How much does AllChinaBuy shipping cost?',
    a: 'Shipping costs vary by destination, weight, dimensions, and shipping method. Our shipping guides explain how to estimate and compare costs.',
  },
  {
    q: 'Where can I find AllChinaBuy coupons?',
    a: 'Coupons and discount codes may be available through various channels. We update our coupon guide with verified information when available.',
  },
  {
    q: 'What is the AllChinaBuy warehouse?',
    a: 'The warehouse is where products are stored before international shipping. Understanding warehouse processes helps you plan your orders better.',
  },
  {
    q: 'How does AllChinaBuy QC work?',
    a: 'Quality Control (QC) involves inspecting products before they are shipped to you. Our QC guide explains what to check and how to request inspections.',
  },
  {
    q: 'How do I use an AllChinaBuy spreadsheet?',
    a: 'Browse categories, filter by product type, compare sellers, and use the shipping calculator to estimate costs before placing an order.',
  },
];

export function injectHomeFaqJsonLd(): void {
  inject('route-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });
}